'use client';
import {
  ComponentProps,
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

import { styled } from '@mui/material/styles';
import { fetchValues, LocaleLanguage, Values } from '@repo/i18n';
import { useQuery } from '@tanstack/react-query';
import {
  NextIntlClientProvider as BaseNextIntlClientProvider,
  RichTranslationValues,
} from 'next-intl';
import { useLocalStorage } from 'usehooks-ts';

const B = styled('b')`
  font-weight: bold;
`;

const defaultTranslationValues: RichTranslationValues = {
  b: (chunks) => <B>{chunks}</B>,
};

export const LocaleContext = createContext<{
  locale: LocaleLanguage;
  setLocale: (value: LocaleLanguage) => void;
  availableLocales: Array<{ label: string; value: LocaleLanguage }>;
  messages: Values;
}>({
  locale: 'en',
  setLocale: () => {
    throw new Error('Not initialized');
  },
  availableLocales: [],
  messages: {} as any,
});

const LOCALE_LABELS: Record<LocaleLanguage, string> = {
  en: 'English',
  ja: '日本',
  cn: '中文',
};

const AVAILABLE_LOCALES = Object.entries(LOCALE_LABELS).map(
  ([value, label]) => ({
    label,
    value: value as LocaleLanguage,
  }),
);

const LocaleProvider: FC<
  PropsWithChildren<{
    initial: { values: Values; locale: LocaleLanguage };
  }>
> = ({ children, initial }) => {
  const [locale, setLocale] = useLocalStorage<LocaleLanguage>(
    'user-locale',
    initial.locale,
  );
  const { data: messages } = useQuery({
    queryKey: ['remote-i18n-values', { locale }],
    queryFn: () => fetchValues(locale),
    initialData: initial.values,
  });

  if (!messages) {
    throw new Error(
      'LocaleProvider should have initial data to provide to intl module',
    );
  }

  return (
    <LocaleContext.Provider
      value={{
        locale,
        setLocale,
        messages,
        availableLocales: AVAILABLE_LOCALES,
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
};

const InnerNextIntlProvider: FC<PropsWithChildren> = ({ children }) => {
  const { locale, messages } = useContext(LocaleContext);
  const [timeZone, setTimeZone] = useState('Europe/Paris');

  useEffect(() => {
    setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, [setTimeZone]);

  return (
    <BaseNextIntlClientProvider
      messages={messages}
      defaultTranslationValues={defaultTranslationValues}
      locale={locale}
      timeZone={timeZone}
    >
      {children}
    </BaseNextIntlClientProvider>
  );
};

export const NextIntlProvider: FC<ComponentProps<typeof LocaleProvider>> = ({
  children,
  initial,
}) => (
  <LocaleProvider initial={initial}>
    <InnerNextIntlProvider>{children}</InnerNextIntlProvider>
  </LocaleProvider>
);

export const useLocale = () => useContext(LocaleContext);
